import React, { useEffect, useState, ChangeEvent, useMemo, useCallback, useRef } from 'react';
import { TablePagination } from '@mui/material';
import { CustomTableBody } from '../../shared/EquipmentTable/CustomTableBody';
import { CustomTableHead } from '../../shared/EquipmentTable/CustomTableHead';
import { Columns, DateFiltersByField, TemplateFilterInput } from '@/types';
import {
  TablePaginationContainer,
  StyledTableContainer,
  ScrollableBox,
  StyledTable,
  PaginationBox,
} from './styles';
import { getEquipmentColumnsFromAssemblyTemplate } from '@/utils';
import { getPoiIconsColumn } from '@/utils/getPoiColumns';
import { useDragToScroll, useRouter, useInsightsPageParams } from '@/hooks';
import * as state from '@/components/Analysis/state';
import { useSelectedAssemblyTemplateId } from '@/components/Analysis/hooks/useSelectedAssemblyTemplateId';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import {
  GetAssembliesByTemplateFieldsFiltersForFilterSideBarQuery,
  useGetAssembliesByTemplateFieldsFiltersForFilterSideBarLazyQuery,
  useGetUserPreferencesQuery,
  Assembly,
  TemplateFieldTypeEnum
} from '@/__generated__/graphql';
import { useUnitConversion } from '@/hooks/useUnitConversion';
import { ColumnsSelectionDialog } from '../ColumnsSelectionDialog';
import { ErrorLogger } from '@/utils/error-logger';
import {
  INSIGHTS_GET_FILTERED_ASSEMBLIES_ERROR,
  INSIGHTS_USER_PREFERENCE_ERROR,
} from '@/constants/Errors';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import useLiveSyncAllPois from '../../shared/hooks/useLiveSyncAllPois';
import { EditCellModal } from '../../shared/EquipmentTable/EditCellModal';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
type Props = {
  saveSingleEdit?: (itemId: string, columnKey: string, value: string | unknown) => Promise<void>;
  saveBulkEdit?: (columnKey: string, value: string | unknown, itemIds: string[]) => Promise<void>;
};
const getView3DColumn = (
  router: ReturnType<typeof useRouter>,
  structureId: string,
  setSelectedAssemblyId: (id: string | undefined) => void
): Columns => ({
  isSortable: false,
  visibility: true,
  key: 'view3D',
  columnName: 'View 3D',
  displayConfig: {
    type: '3DBUTTON',
  },
  onCellClick: (event, item) => {
    event.stopPropagation();
    setSelectedAssemblyId(undefined);

    const url = `/analysis/${structureId}/viewer?assembly=${item?.tagName}`;

    const isCtrlClick = event.ctrlKey || event.metaKey;
    if (isCtrlClick) window.open(url, '_blank');
    else router.push(url);
  },
});

export const EquipmentTable = ({ saveSingleEdit, saveBulkEdit }: Props) => {
  const [page, setPage] = useState(0);
  const [allColumns, setAllColumns] = useState<Columns[]>([]);
  const [visibleColumns, setVisibleColumns] = useState<string[]>([]);
  const setInsightsExplorerLoading = useSetAtom(state.insightsExplorerLoadingState);
  const [allAssemblies, setAllAssemblies] =
    useState<
      GetAssembliesByTemplateFieldsFiltersForFilterSideBarQuery['getAssembliesByTemplateFieldsFilters']
    >();
  const [assembliesCount, setAssembliesCount] = useAtom(state.assembliesCount);
  const [loadingTable, setLoadingTable] = useState(true);
  const setAssembliesLoading = useSetAtom(state.assembliesLoading);
  const [selectedAssemblyIds, setSelectedAssemblyIds] = useAtom(state.selectedAssemblyIds);
  const [dialogOpen, setDialogOpen] = useState(false);
  const assemblyTemplate = useAtomValue(state.assemblyTemplate);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const templateId = useSelectedAssemblyTemplateId()!;
  const selectedTemplateFilters = useAtomValue(state.selectedTemplateFiltersState);
  const appliedDateFiltersValue = useAtomValue(state.appliedDateFilters);
  const filteredDeckIds = useAtomValue(state.selectedFilteredDeckIds);
  const selectedPaintRegionIds = useAtomValue(state.selectedFilteredPaintRegionIds);
  const selectedWorkpackIds = useAtomValue(state.selectedFilteredWorkpackIds);
  const selectedPoiTypes = useAtomValue(state.selectedFilteredPoiTypes);
  const setAppliedAssemblyFilters = useSetAtom(state.appliedAssemblyFilters);
  const [appliedTemplateFilters, setAppliedTemplateFilters] = useState(selectedTemplateFilters);
  const [appliedDeckFilters, setAppliedDeckFilters] = useState(filteredDeckIds);
  const [appliedPaintRegionFilters, setAppliedPaintRegionFilters] =
    useState(selectedPaintRegionIds);
  const [appliedWorkpackFilters, setAppliedWorkpackFilters] = useState(selectedWorkpackIds);
  const [appliedPoiTypeFilters, setAppliedPoiTypeFilters] = useState(selectedPoiTypes);
  const setAllComponentsSelected = useSetAtom(state.isAllComponentsSelected);
  const { showSnackBar } = useSnackBarMessage({});
  const [headerEditOpen, setHeaderEditOpen] = useState(false);
  const [headerEditColumnKey, setHeaderEditColumnKey] = useState<string>('');
  const [headerEditColumnName, setHeaderEditColumnName] = useState<string>('');
  const appliedFilters = useAtomValue(state.filters);

  const selectedTemplateFiltersWithDates = useMemo(() => {
    const dateFiltersAsTemplateFilters: TemplateFilterInput[] = Object.entries(appliedDateFiltersValue).flatMap(
      ([fieldKey, filter]) => {
        if (filter.type === 'single' && filter.date) {
          return [{ id: fieldKey, value: [], startDate: filter.date, endDate: filter.date }];
        }
        if (filter.type === 'range' && filter.startDate && filter.endDate) {
          return [
            {
              id: fieldKey,
              value: [],
              startDate: filter.startDate,
              endDate: filter.endDate,
            },
          ];
        }
        return [];
      }
    );

    return [...selectedTemplateFilters, ...dateFiltersAsTemplateFilters];
  }, [appliedDateFiltersValue, selectedTemplateFilters]);

  // Sync paint region filters with global state
  useEffect(() => {
    setAppliedPaintRegionFilters(selectedPaintRegionIds);
    setAppliedAssemblyFilters((prev) => ({
      ...prev,
      paintRegionFilters: selectedPaintRegionIds,
    }));
  }, [selectedPaintRegionIds, setAppliedAssemblyFilters]);

  const [applyFilters, setApplyFilters] = useAtom(state.applyFilters);
  const structureId = useGetStructureId();
  const [sortDirection, setSortDirection] = useState<number>(1);
  const [sortBy, setSortBy] = useState('createdAt');
  const sortByReference = useRef(sortBy);
  const {
    data: userPreferenceData,
    error: userPreferenceError,
    loading: userPreferenceLoading,
  } = useGetUserPreferencesQuery({
    fetchPolicy: 'cache-first',
  });
  const [selectedAssemblyId, setSelectedAssemblyId] = useAtom(state.selectedAssemblyId);
  const setSelectedAssemblyName = useSetAtom(state.selectedAssemblyName);
  const { getUnitSuffix } = useUnitConversion();

  // Use the callback to handle refetch completion since we are using a lazy query
  // which does not trigger onCompleted when the document is refetched
  useLiveSyncAllPois(() => {
    if (structureId && templateId) {
      fetchFilteredAssemblies({
        variables: {
          input: {
            selectedTemplateFilters: appliedTemplateFilters,
            structureId,
            templateId,
            selectedDeckIds: appliedDeckFilters,
            selectedPaintRegionIds: appliedPaintRegionFilters,
            selectedWorkpackIds: appliedWorkpackFilters,
            ...(appliedPoiTypeFilters.length > 0 && {
              selectedPoiTypes: appliedPoiTypeFilters,
            }),
          },
          pageInfo: {
            skip: page * rowsPerPage,
            limit: rowsPerPage,
            sortBy,
            sortDir: sortDirection,
          },
        },
      });
    }
  });

  const { containerReference, handleMouseDown } = useDragToScroll();

  const [fetchFilteredAssemblies] =
    useGetAssembliesByTemplateFieldsFiltersForFilterSideBarLazyQuery({
      fetchPolicy: 'network-only',
      onCompleted: (data) => {
        // Clear previous data before setting new data
        setAllAssemblies([]);
        setAllAssemblies(data?.getAssembliesByTemplateFieldsFilters);
        setAssembliesCount(data?.getAssembliesCount?.count ?? 0);
        setApplyFilters(false);
        setInsightsExplorerLoading(false);
        setLoadingTable(false);
      },
      onError: (err: Error) => {
        ErrorLogger.log(
          INSIGHTS_GET_FILTERED_ASSEMBLIES_ERROR.withContext({
            input: {
              selectedTemplateFilters: appliedTemplateFilters,
              structureId,
              templateId,
              selectedDeckIds: appliedDeckFilters,
              selectedPaintRegionIds: appliedPaintRegionFilters,
              selectedWorkpackIds: appliedWorkpackFilters,
              ...(appliedPoiTypeFilters.length > 0 && {
                selectedPoiTypes: appliedPoiTypeFilters,
              }),
            },
            pageInfo: {
              skip: page * rowsPerPage,
              limit: rowsPerPage,
              sortBy,
              sortDir: sortDirection,
            },
          }).withError(err)
        );
        setApplyFilters(false);
        setInsightsExplorerLoading(false);
        setLoadingTable(false);
      },
    });

  const getFilteredAssemblies = useCallback(() => {
    if (structureId && templateId) {
      setAssembliesLoading(true);
      fetchFilteredAssemblies({
        variables: {
          input: {
            selectedTemplateFilters: appliedTemplateFilters,
            structureId,
            templateId,
            selectedDeckIds: appliedDeckFilters,
            selectedPaintRegionIds: appliedPaintRegionFilters,
            selectedWorkpackIds: appliedWorkpackFilters,
            ...(appliedPoiTypeFilters.length > 0 && {
              selectedPoiTypes: appliedPoiTypeFilters,
            }),
          },
          pageInfo: {
            skip: page * rowsPerPage,
            limit: rowsPerPage,
            sortBy,
            sortDir: sortDirection,
          },
        },
      });
    }
  }, [
    appliedDeckFilters,
    appliedPaintRegionFilters,
    appliedTemplateFilters,
    appliedWorkpackFilters,
    appliedPoiTypeFilters,
    fetchFilteredAssemblies,
    page,
    rowsPerPage,
    setAssembliesLoading,
    sortBy,
    sortDirection,
    structureId,
    templateId,
  ]);

  useEffect(() => {
    if (templateId && applyFilters) {
      getFilteredAssemblies();
    }
  }, [applyFilters, getFilteredAssemblies, templateId]);

  useEffect(() => {
    if (sortByReference.current !== sortBy) {
      getFilteredAssemblies();
    }
  }, [getFilteredAssemblies, sortBy]);

  useEffect(() => {
    getFilteredAssemblies();
  }, [getFilteredAssemblies, rowsPerPage, page]);

  useEffect(() => {
    if (applyFilters) {
      setAppliedTemplateFilters(selectedTemplateFiltersWithDates);
      setAppliedDeckFilters(filteredDeckIds);
      setAppliedPaintRegionFilters(selectedPaintRegionIds);
      setAppliedWorkpackFilters(selectedWorkpackIds);
      setAppliedPoiTypeFilters(selectedPoiTypes);
      setPage(0);
      setAppliedAssemblyFilters((prev) => ({
        ...prev,
        templateFilters: selectedTemplateFiltersWithDates,
        deckFilters: filteredDeckIds,
        paintRegionFilters: selectedPaintRegionIds,
        workpackFilters: selectedWorkpackIds,
        poiTypeFilters: selectedPoiTypes,
      }));
    }
  }, [
    templateId,
    selectedTemplateFilters,
    selectedTemplateFiltersWithDates,
    filteredDeckIds,
    selectedPaintRegionIds,
    selectedWorkpackIds,
    selectedPoiTypes,
    applyFilters,
    fetchFilteredAssemblies,
    structureId,
    page,
    rowsPerPage,
    setAppliedAssemblyFilters,
  ]);

  const router = useRouter();

  useEffect(() => {
    const assemblyFromUrl = router.query?.assembly;
    if (assemblyFromUrl && allAssemblies?.length) {
      const found = allAssemblies.find((a) => a.tagName === assemblyFromUrl);
      if (found) {
        setSelectedAssemblyId(found.id);
        setSelectedAssemblyName(found.tagName);
      }
    }
  }, [router.query?.assembly, allAssemblies, setSelectedAssemblyId, setSelectedAssemblyName]);

  const dateFieldKeys = useMemo(() => {
    if (!assemblyTemplate?.fields) return [];
    return assemblyTemplate.fields
      .filter((f) => f.type === TemplateFieldTypeEnum.Date && f.key)
      .map((f) => f.key as string);
  }, [assemblyTemplate]);

  const parseDateOnly = (raw: string) => {
    const datePart = raw.slice(0, 10);
    const [y, m, d] = datePart.split('-').map(Number);
    if (!y || !m || !d) return Number.NaN;
    return new Date(y, m - 1, d).getTime();
  };

  const displayedAssemblies = useMemo(() => {
    if (!allAssemblies) return allAssemblies;
    const dateFilters: DateFiltersByField | undefined = appliedFilters?.dateFilter;
    if (!dateFilters || dateFieldKeys.length === 0) return allAssemblies;
    const activeDateFilters = Object.entries(dateFilters).filter(([fieldKey]) =>
      dateFieldKeys.includes(fieldKey)
    );
    if (!activeDateFilters.length) return allAssemblies;

    return allAssemblies.filter((assembly) => {
      const fieldData = assembly.templateFieldData as Record<string, string> | undefined;
      if (!fieldData) return false;

      return activeDateFilters.every(([fieldKey, dateFilter]) => {
        const rawValue = fieldData[fieldKey];
        if (!rawValue) return false;
        const fieldDate = parseDateOnly(rawValue);
        if (Number.isNaN(fieldDate)) return false;

        if (dateFilter.type === 'single') {
          const target = parseDateOnly(dateFilter.date);
          return fieldDate === target;
        }
        const start = parseDateOnly(dateFilter.startDate);
        const end = parseDateOnly(dateFilter.endDate);
        return fieldDate >= start && fieldDate <= end;
      });
    });
  }, [allAssemblies, appliedFilters?.dateFilter, dateFieldKeys]);

  const initialColumns = useMemo(() => {
    if (!assemblyTemplate) return [];

    const columnsFromTemplate = getEquipmentColumnsFromAssemblyTemplate(assemblyTemplate);
    const poiIconsColumn = getPoiIconsColumn();
    const view3dColumn = [getView3DColumn(router, structureId, setSelectedAssemblyId)];

    return [
      {
        isSortable: true,
        visibility: true,
        key: 'tagName',
        columnName: 'Component',
      },
      ...columnsFromTemplate,
      poiIconsColumn,
      ...view3dColumn,
    ] as Columns[];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assemblyTemplate, router, structureId]);

  useEffect(() => {
    setAllColumns((prevColumns) => {
      if (visibleColumns.length > 0) {
        return initialColumns.map((column) => ({
          ...column,
          visibility: visibleColumns.includes(column.columnName),
        }));
      }

      if (prevColumns.length === 0 || prevColumns.length !== initialColumns.length) {
        const defaultColumns = new Set(
          initialColumns.filter(({ visibility }) => visibility).map(({ columnName }) => columnName)
        );

        return initialColumns.map((column) => ({
          ...column,
          visibility: defaultColumns.has(column.columnName),
        }));
      }

      return initialColumns.map((column) => {
        const existingColumn = prevColumns.find((c) => c.columnName === column.columnName);
        return {
          ...column,
          visibility: existingColumn?.visibility ?? column.visibility,
        };
      });
    });
  }, [initialColumns, visibleColumns]);

  useEffect(() => {
    if (userPreferenceLoading) {
      return;
    }

    if (userPreferenceError) {
      ErrorLogger.log(
        INSIGHTS_USER_PREFERENCE_ERROR.withContext({ structureId }).withError(userPreferenceError)
      );
    }

    const preferencesList = userPreferenceData?.getUserPreferences?.structure?.[structureId];
    const userColumns = preferencesList?.columns?.COMPONENT ?? [];

    let nextVisible =
      userColumns.length > 0
        ? [...userColumns]
        : initialColumns
            .filter((c) => c.visibility)
            .slice(0, 8)
            .map((c) => c.columnName);

    if (!nextVisible.includes('Component')) {
      nextVisible = ['Component', ...nextVisible];
    }

    setVisibleColumns(nextVisible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    structureId,
    userPreferenceData?.getUserPreferences?.structure,
    userPreferenceError,
    userPreferenceLoading,
  ]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const toggleDialog = useCallback(() => {
    setDialogOpen(!dialogOpen);
  }, [dialogOpen]);

  const handleRowCheckBoxChange = useCallback(
    (assemblyId: string) => {
      if (selectedAssemblyIds.includes(assemblyId)) {
        setSelectedAssemblyIds(
          selectedAssemblyIds.filter((selectedId) => selectedId !== assemblyId)
        );
      } else {
        setSelectedAssemblyIds([...selectedAssemblyIds, assemblyId]);
      }
    },
    [selectedAssemblyIds, setSelectedAssemblyIds]
  );

  const headerCheckBoxChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked && displayedAssemblies?.length) {
        setSelectedAssemblyIds(displayedAssemblies.map((assembly) => assembly.id));
        setAllComponentsSelected(true);
      } else {
        setSelectedAssemblyIds([]);
        setAllComponentsSelected(false);
      }
    },
    [setSelectedAssemblyIds, displayedAssemblies, setAllComponentsSelected]
  );

  const handleSort = useCallback(
    (property: string) => {
      const isAsc = sortBy === property && sortDirection === 1;
      setSortDirection(isAsc ? -1 : 1);
      setSortBy(property);
    },
    [sortDirection, sortBy]
  );

  const activeColumns = useMemo(() => {
    return allColumns
      .filter(({ visibility }) => visibility)
      .map((column) => {
        const { displayUnit, unit, columnName, templateFieldKey } = column;
        const unitSuffix = getUnitSuffix({
          unit: displayUnit ?? unit,
          field: templateFieldKey,
        });
        let upperCaseColumnName = columnName.toUpperCase();
        upperCaseColumnName = unitSuffix
          ? `${upperCaseColumnName} (${unitSuffix})`
          : upperCaseColumnName;
        return { ...column, columnName: upperCaseColumnName };
      });
  }, [allColumns, getUnitSuffix]);

  const dialogColumns = useMemo(() => {
    return allColumns.map(
      ({
        columnName,
        visibility,
        displayUnit,
        unit,
        templateFieldKey,
        enableInlineEdit,
        metaData,
      }) => {
        return {
          name: columnName,
          key: columnName,
          isSelected: visibility,
          displayUnit: displayUnit ?? '',
          unit: unit ?? '',
          templateFieldKey,
          enableInlineEdit,
          metaData,
        };
      }
    );
  }, [allColumns]);

  const handleApplyColumns = useCallback((selectedColumnsKeys: string[]) => {
    setVisibleColumns(selectedColumnsKeys);
    const selectedColumnsSet = new Set(selectedColumnsKeys);
    setAllColumns((previous) =>
      previous.map((column) => ({
        ...column,
        visibility: selectedColumnsSet.has(column.columnName ?? ''),
      }))
    );
  }, []);

  const onOpenHeaderEditModal = useCallback((columnKey: string, columnName: string) => {
    setHeaderEditColumnKey(columnKey);
    setHeaderEditColumnName(columnName);
    setHeaderEditOpen(true);
  }, []);

  const onHeaderEditSave = useCallback(
    async (newValue: unknown) => {
      if (!saveBulkEdit || !headerEditColumnKey) {
        setHeaderEditOpen(false);
        return;
      }
      await saveBulkEdit(headerEditColumnKey, newValue, selectedAssemblyIds);
      setHeaderEditOpen(false);
    },
    [saveBulkEdit, headerEditColumnKey, selectedAssemblyIds]
  );

  const onHeaderEditSuccess = useCallback(() => {
    showSnackBar('Table values are edited successfully', 'success');
    getFilteredAssemblies();
  }, [showSnackBar, getFilteredAssemblies]);
  const onHeaderEditError = useCallback(() => {
    showSnackBar('Failed to edit table values', 'error');
  }, [showSnackBar]);

  const { setAssembly } = useInsightsPageParams();

  const handleRowClick = useCallback(
    (event: React.MouseEvent<HTMLTableRowElement, MouseEvent>, assembly: Assembly) => {
      if ((event.target as HTMLInputElement).type === 'checkbox') {
        return;
      }
      setSelectedAssemblyId(assembly.id);
      setSelectedAssemblyName(assembly.tagName);
      setAssembly(assembly.tagName, '0');
    },
    [setSelectedAssemblyId, setSelectedAssemblyName, setAssembly]
  );

  return (
    <>
      <EditCellModal
        open={headerEditOpen}
        onClose={() => setHeaderEditOpen(false)}
        saveBulkEdit={onHeaderEditSave}
        cellData={{ columnName: headerEditColumnName, columnKey: headerEditColumnKey }}
        onSuccess={onHeaderEditSuccess}
        onError={onHeaderEditError}
      />
      <ColumnsSelectionDialog
        open={dialogOpen}
        columns={dialogColumns}
        heading="Enable the columns you would like to see in the table"
        subHeading="Select the columns you want to display:"
        applyButtonText="Apply"
        userPreferenceKey="COMPONENT"
        userPreferenceType="columns"
        onApply={handleApplyColumns}
        onClose={() => setDialogOpen(false)}
      />

      <StyledTableContainer>
        <ScrollableBox ref={containerReference} onMouseDown={handleMouseDown}>
          <StyledTable stickyHeader>
            <CustomTableHead
              orderBy={sortBy}
              order={sortDirection}
              activeColumns={activeColumns}
              toggleDialog={toggleDialog}
              handleHeaderCheckboxChange={headerCheckBoxChange}
              handleSort={handleSort}
              selectedRowIds={selectedAssemblyIds}
              onOpenHeaderEditModal={onOpenHeaderEditModal}
            />

            <CustomTableBody<Assembly>
              loading={loadingTable}
              data={displayedAssemblies as Assembly[]}
              activeColumns={activeColumns}
              onCheckboxChange={handleRowCheckBoxChange}
              selectedRow={selectedAssemblyId}
              onRowClick={handleRowClick}
              selectedIds={selectedAssemblyIds}
              saveSingleEdit={saveSingleEdit}
              refetchAssemblies={getFilteredAssemblies}
            />
          </StyledTable>
        </ScrollableBox>
        <PaginationBox>
          <TablePaginationContainer>
            <TablePagination
              count={assembliesCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPageOptions={[
                { label: '10', value: 10 },
                { label: '20', value: 20 },
                { label: '50', value: 50 },
                { label: '100', value: 100 },
                { label: 'All', value: assembliesCount },
              ]}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              data-testid="equipment-table-pagination"
            />
          </TablePaginationContainer>
        </PaginationBox>
      </StyledTableContainer>
    </>
  );
};
